import { DateTime, type DurationLikeObject } from "luxon";

import LocalForagePlugin, { syncState } from "@/plugins/LocalForagePlugin";
import { batchSetItems, batchRemoveItems } from "@/utils/localforage-batch";
import { goFetch } from "@/utils/network-utils";
import type { ApiFetchResponse, PersistedQueryPayload } from "@tmi/types/api/fetch";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.DEBUG ) );

export interface DependencySyncConfig<TResponse, TApi, TLocal extends { id: string }> {
    name: string;
    store: LocalForage;
    syncKey: string;
    throttle: DurationLikeObject;
    queryId: string;
    pageSize: number;
    endpoint?: string;
    serverBaseUri?: string;
    extractPayload: ( data: ApiFetchResponse<TResponse> ) => TApi[] | undefined | null;
    mapItem: ( item: TApi ) => TLocal;
    postProcess?: ( items: TLocal[] ) => TLocal[];
}

export async function fetchFromServer<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    authToken?: string | null
): Promise<TLocal[]> {
    const t0 = performance.now();
    let allItems: TLocal[] = [];
    let page = 0;
    let pagePayload: TLocal[] = [];

    do {
        const response =
            await goFetch<TResponse, PersistedQueryPayload>( {
                endpoint: config.endpoint ?? `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                serverBaseUri: config.serverBaseUri,
                payload: {
                    id: config.queryId,
                    variables: {
                        skip: page,
                        limit: config.pageSize
                    }
                },
                authToken: authToken ?? undefined
            } );

        const raw = response ? config.extractPayload( response ) : undefined;
        if ( raw ) {
            pagePayload = raw.map( config.mapItem );

            const t1 = performance.now();
            logger.info( `Fetching & mapping ${ pagePayload.length } ${ config.name } items took ${ t1 - t0 } ms` );

            allItems = [ ...pagePayload, ...allItems ];
        } else {
            pagePayload = [];
        }

        page++;
    } while ( pagePayload.length >= config.pageSize );

    if ( config.postProcess ) {
        allItems = config.postProcess( allItems );
    }

    return allItems;
}

export async function syncDependency<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    map?: Map<string, TLocal>,
    authToken?: string | null
): Promise<boolean> {
    let updated = false;
    try {
        const t0 = performance.now();

        if ( typeof config.store === 'undefined' || typeof syncState === 'undefined' ) {
            await LocalForagePlugin.initialize();
        }

        await syncState.ready();
        const syncThresholdInMillis = await syncState.getItem<number>( config.syncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            const received = await fetchFromServer( config, authToken );

            if ( received.length > 0 ) {
                const nextSyncThresholdInMillis = DateTime.utc().plus( config.throttle ).toMillis();
                await syncState.setItem( config.syncKey, nextSyncThresholdInMillis );
                updated = true;

                await config.store.ready();

                // Purge IDB entries that no longer exist on the server
                const receivedIds = new Set( received.map( item => item.id ) );
                const keysToRemove: string[] = [];
                await config.store.iterate( ( _: TLocal, key: string ) => {
                    if ( !receivedIds.has( key ) ) { keysToRemove.push( key ); }
                    return;
                } );
                await batchRemoveItems( config.store, keysToRemove );

                await batchSetItems( config.store, received.map( item => [ item.id, item ] as const ) );

                const t1 = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ received.length } ${ config.name } items from server in ${ t1 - t0 } ms (${ keysToRemove.length } stale removed)` );

                // Populate map directly from server data — skip redundant IDB re-read
                if ( map ) {
                    map.clear();
                    received.forEach( item => map.set( item.id, item ) );
                }
                return updated;
            } else {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( config.syncKey, nextSyncThresholdInMillis );
            }
        } else if ( map && map.size > 0 ) {
            return updated;
        }

        // Cold start: populate map from IDB cache
        if ( map ) {
            map.clear();
            await config.store.ready();
            await config.store.iterate( ( item: TLocal ) => {
                map.set( item.id, item );
                return;
            } );
        }
    } catch ( err: any ) {
        logger.error( `[ ${ config.name }-sync-coordinator ] Sync failed:`, err );
    }

    return updated;
}
